import React from 'react';
import { ArrowUturnLeftIcon, PlusIcon, TrashIcon } from '@heroicons/react/24/outline';
import type { SafetyLookupOption, SafetyTableChildSpec, SafetyTableField } from '../../../types/safetySystem';
import { inputClass, str } from './unitFields';
import { YES_NO_OPTIONS } from './unitForm';
import { type ChildFormRow, newChildRow, norm, rowKey, toInputDateTime } from './tableForm';

/**
 * Generic editable rows for one Safety Tables child spec (contacts, counties, categories).
 * One `<input>` per editable field by kind, labelled "<Column> <n>" (n = the row's position).
 * "Remove" on a saved row sets `deleted='Y'` (the row stays visible, muted, with "Restore");
 * on an unsaved row it drops the row. A child without a soft flag only lets unsaved rows go.
 * `readOnly` renders every control disabled and hides Add / Remove.
 */

export interface ChildRowsEditorProps {
  child: SafetyTableChildSpec;
  rows: ChildFormRow[];
  onRowsChange: (rows: ChildFormRow[]) => void;
  /** Lookup choices keyed by field name (the server's `options`). */
  options: Record<string, SafetyLookupOption[]>;
  readOnly?: boolean;
  emptyText?: string;
}

const th = 'px-1.5 py-1 text-left text-xs font-semibold uppercase tracking-wide text-muted';
const td = 'px-1 py-0.5 align-top';
const cell = `${inputClass} min-w-[5rem]`;

export function ChildRowsEditor({ child, rows, onRowsChange, options, readOnly = false, emptyText = 'No rows.' }: ChildRowsEditorProps) {
  const soft = child.soft_delete;
  const fields = child.fields.filter((f) => f.name !== soft);
  const isDeleted = (r: ChildFormRow) => !!soft && norm(r[soft]).toUpperCase() === 'Y';

  const update = (key: string, patch: Record<string, unknown>) =>
    onRowsChange(rows.map((r) => (rowKey(r) === key ? { ...r, ...patch } : r)));
  const remove = (row: ChildFormRow) => {
    const key = rowKey(row);
    if (!row.recno) { onRowsChange(rows.filter((r) => rowKey(r) !== key)); return; }
    if (soft) update(key, { [soft]: 'Y' });
  };
  const restore = (row: ChildFormRow) => { if (soft) update(rowKey(row), { [soft]: 'N' }); };
  const add = () => onRowsChange([...rows, newChildRow(child)]);

  const control = (f: SafetyTableField, row: ChildFormRow, label: string): React.ReactNode => {
    const key = rowKey(row);
    const disabled = readOnly || !f.editable || isDeleted(row);
    const value = str(row[f.name]);
    const set = (v: string) => update(key, { [f.name]: v });
    const common = { 'aria-label': label, disabled, className: cell };
    switch (f.kind) {
      case 'yn':
        return (
          <select {...common} value={value.toUpperCase() === 'Y' ? 'Y' : 'N'} onChange={(e) => set(e.target.value)}>
            {YES_NO_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        );
      case 'select': {
        const opts = f.options ?? [];
        return (
          <select {...common} value={value} onChange={(e) => set(e.target.value)}>
            {(!f.required || value === '') && <option value="">—</option>}
            {value && !opts.includes(value) && <option value={value}>{value}</option>}
            {opts.filter((o) => o !== '').map((o) => <option key={o} value={o}>{o}</option>)}
          </select>
        );
      }
      case 'lookup': {
        const opts = options[f.name] ?? [];
        const current = value && value !== '0' ? value : '';
        const label0 = norm(row[`${f.name}_label`]);
        return (
          <select {...common} value={current} onChange={(e) => set(e.target.value)}>
            <option value="">—</option>
            {current && !opts.some((o) => String(o.value) === current) && <option value={current}>{label0 || current}</option>}
            {opts.map((o) => <option key={String(o.value)} value={String(o.value)}>{o.label}</option>)}
          </select>
        );
      }
      case 'textarea':
        return <textarea {...common} rows={2} value={value} onChange={(e) => set(e.target.value)} />;
      case 'int':
        return <input {...common} type="number" step="1" value={value} onChange={(e) => set(e.target.value)} />;
      case 'decimal':
        return <input {...common} type="number" step="0.01" inputMode="decimal" value={value} onChange={(e) => set(e.target.value)} />;
      case 'date':
        return <input {...common} type="date" value={value.slice(0, 10)} onChange={(e) => set(e.target.value)} />;
      case 'datetime':
        return <input {...common} type="datetime-local" value={toInputDateTime(value)} onChange={(e) => set(e.target.value)} />;
      default:
        return <input {...common} type="text" value={value} onChange={(e) => set(e.target.value)} />;
    }
  };

  return (
    <div className="space-y-2">
      <div className="overflow-x-auto">
        <table className="w-full border-separate border-spacing-0 text-sm">
          <thead>
            <tr>
              <th className={th}>#</th>
              {fields.map((f) => <th key={f.name} className={th}>{f.label}</th>)}
              {!readOnly && <th className={th} />}
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr><td colSpan={fields.length + 2} className="px-2 py-3 text-center text-sm text-muted">{emptyText}</td></tr>
            )}
            {rows.map((row, i) => {
              const n = i + 1;
              const gone = isDeleted(row);
              const canRemove = !readOnly && (!row.recno || !!soft);
              return (
                <tr key={rowKey(row)} className={gone ? 'text-muted' : ''} data-deleted={gone ? '1' : undefined}>
                  <td className={`${td} pt-2 text-xs text-muted`}>{n}</td>
                  {fields.map((f) => <td key={f.name} className={td}>{control(f, row, `${f.label} ${n}`)}</td>)}
                  {!readOnly && (
                    <td className={`${td} whitespace-nowrap`}>
                      {gone ? (
                        <button type="button" aria-label={`Restore ${n}`} onClick={() => restore(row)}
                          className="inline-flex items-center gap-1 rounded px-1.5 py-1 text-xs text-muted hover:bg-highlight hover:text-heading">
                          <ArrowUturnLeftIcon width={14} aria-hidden="true" />Restore
                        </button>
                      ) : canRemove ? (
                        <button type="button" aria-label={`Remove ${n}`} onClick={() => remove(row)}
                          className="rounded p-1 text-muted hover:bg-highlight hover:text-danger-fg">
                          <TrashIcon width={16} aria-hidden="true" />
                        </button>
                      ) : null}
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {!readOnly && (
        <button type="button" onClick={add}
          className="inline-flex items-center gap-1 rounded-md border border-rule px-2 py-1 text-sm hover:bg-highlight">
          <PlusIcon width={14} aria-hidden="true" />{`Add ${child.title}`}
        </button>
      )}
    </div>
  );
}

export default ChildRowsEditor;
